import { ReplaceStencilOp, Sprite, sRGBEncoding, Texture, Vector3 } from 'three';
import {
  generateClassInstance,
  ObjectConfig,
} from '../types/custom-component.type';
import { ThreeObjectBase } from './three-object.base';

export type SpriteState = 'default' | 'selected';

/**
 * Translucent texture drawn around the sprite while it is selected (e.g. a ring + halo).
 * It must be transparent in the middle: it is rendered on a child sprite centered on the
 * icon, `scale` times bigger than the icon.
 */
export type SpriteHalo = { icon: string; scale?: number };

/** Marks the halo child in `userData` so scene inspectors can tell it from the icon. */
const HALO_USER_DATA_KEY = 'simlabHalo';

export class SpriteConfiguration implements ObjectConfig<SpriteComponent> {
  private _instance!: SpriteComponent;
  constructor(
    readonly config: {
      icon: string;
      /** Ring / halo shown around the icon while the component is selected. */
      selectedHalo?: SpriteHalo;
      scale?: Vector3;
      position?: Vector3;
      visible?: boolean | undefined;
      renderOrder?: number | undefined;
      opacity?: number | undefined;
      isCollider?: boolean | undefined;
      transparent?: boolean | undefined;
    }
  ) { }
  init(threeContext: typeof import('three')) {
    this._instance = generateClassInstance<
      SpriteComponent,
      SpriteConfiguration
    >(SpriteComponent, threeContext, this);
  }
  get instance() {
    return this._instance;
  }
}
/**
 * Icon sprite with an optional selection halo.
 *
 * The icon itself keeps the material it was configured with (opaque by default). Opaque
 * sprites are drawn in the opaque pass and write depth, so scan overlays drawn later in
 * the transparent pass (e.g. Matterport sweep pucks, `transparent` + `depthWrite: false`)
 * are hidden behind the icon. The halo needs alpha blending, so it lives on a separate
 * child sprite that is only attached while the component is selected; that keeps the icon
 * itself out of the transparent pass.
 */
export class SpriteComponent extends ThreeObjectBase {
  set rotation(position: THREE.Euler) {
    throw new Error('Method not implemented.');
  }
  private _sprite!: THREE.Sprite;
  private _halo?: THREE.Sprite;
  private _state: SpriteState = 'default';
  /** Textures per url, loaded once and kept alive so state changes never leave the sprite without a map. */
  private readonly _textures = new Map<string, Texture>();
  constructor(
    private readonly three: typeof import('three'),
    private readonly configuration: SpriteConfiguration['config']
  ) {
    super();
    this.init(three, configuration);
  }

  init(
    threeContext: typeof import('three'),
    configuration: SpriteConfiguration['config']
  ): void {
    this._sprite = this._createObject(threeContext);
    this._icon = this.configuration.icon;
    this._sprite.layers.set(3);
    this._sprite.rotateX(Math.PI * 0.5);
    configuration.position ?? ({ x: 0, y: 0, z: 0 } as Vector3);
    configuration.selectedHalo && this._texture(configuration.selectedHalo.icon);
    this._applyState();
  }

  private _texture(iconPath: string): Texture {
    let texture = this._textures.get(iconPath);
    if (!texture) {
      texture = this._loadMaterial(iconPath);
      texture.encoding = sRGBEncoding;
      this._textures.set(iconPath, texture);
    }
    return texture;
  }

  private set _icon(iconPath: string) {
    const material = this._sprite.material;
    material.map = this._texture(iconPath);
    material.needsUpdate = true;
    this.configuration.icon = iconPath;
  }

  set icon(iconPath: string) {
    if (iconPath !== this.configuration.icon) {
      this._icon = iconPath;
    }
  }

  set selectedHalo(halo: SpriteHalo | undefined) {
    this.configuration.selectedHalo = halo;
    halo && this._texture(halo.icon);
    this._applyState();
  }

  /** Releases every cached texture and the halo material; call when the sprite leaves the scene. */
  disposeTextures() {
    this._textures.forEach((texture) => texture.dispose());
    this._textures.clear();
    if (this._halo) {
      this._halo.removeFromParent();
      this._halo.material.dispose();
      this._halo = undefined;
    }
  }

  get state(): SpriteState {
    return this._state;
  }

  /** Attaches the selection halo (when configured) for `selected`, detaches it otherwise. */
  setState(state: SpriteState) {
    if (state === this._state) return;
    this._state = state;
    this._applyState();
  }

  private _applyState() {
    const halo = this.configuration.selectedHalo;
    if (this._state === 'selected' && halo) {
      this._showHalo(halo);
    } else {
      this._halo?.removeFromParent();
    }
  }

  private _showHalo(halo: SpriteHalo) {
    if (!this._halo) {
      this._halo = this._createHalo();
    }
    const material = this._halo.material;
    const texture = this._texture(halo.icon);
    if (material.map !== texture) {
      material.map = texture;
      material.needsUpdate = true;
    }
    const factor = halo.scale ?? 1;
    this._halo.scale.set(factor, factor, 1);
    // Same name and layers as the icon so raycasts / occlusion checks treat both as one marker.
    this._halo.name = this._sprite.name;
    this._halo.layers.mask = this._sprite.layers.mask;
    if (this._halo.parent !== this._sprite) this._sprite.add(this._halo);
  }

  private _createHalo(): Sprite {
    const material = new this.three.SpriteMaterial({
      transparent: true,
      depthWrite: false,
      alphaTest: 0.01,
      toneMapped: false,
    });
    const halo = new this.three.Sprite(material);
    halo.userData[HALO_USER_DATA_KEY] = true;
    // drawn after the icon within the transparent pass; the icon still wins the depth test
    halo.renderOrder = 1;
    return halo;
  }

  private _createObject(threeContext: typeof import('three')): Sprite {
    const material = new threeContext.SpriteMaterial({
      opacity: this.configuration.opacity ?? 1,
      transparent: this.configuration.transparent ?? false,
      alphaTest: 0.2,
      toneMapped: false,
    });
    material.stencilWrite = true;
    material.stencilZPass = ReplaceStencilOp;
    return new threeContext.Sprite(material);
  }
  get object3D(): Sprite {
    return this._sprite;
  }
  set position(position: Vector3) {
    this._sprite.position.set(position.x, position.y, position.z);
  }
  set scale(scale: Vector3) {
    const { x, y, z } = scale;
    this._sprite.scale.set(x, y, z);
  }

  hide() {
    if (this._sprite.visible) {
      this._sprite.visible = false;
    }
  }

  show() {
    if (!this._sprite.visible) {
      this._sprite.visible = true;
    }
  }

  private _loadMaterial(iconPath: string) {
    return (new this.three.TextureLoader() as THREE.TextureLoader).load(
      iconPath
    );
  }
}
